// =============================================================================
// MeuDriver — Upload de documento de identidade (RG / CNH / Passaporte)
// =============================================================================
// Bucket `identity-docs` PRIVADO. Path: `{auth.uid()}/{uuid}.{ext}`.
// Status controlado pelo admin via AdminIdentityDocReviewButton.
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, ShieldCheck, Upload, IdCard } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type DocStatus = 'not_submitted' | 'pending' | 'verified' | 'rejected'

const STATUS_META: Record<Exclude<DocStatus, 'not_submitted'>, { label: string; cls: string }> = {
  pending:  { label: 'Aguardando análise', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Verificado',         cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejeitado',          cls: 'bg-red-100 text-red-800 border-red-200' },
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function InstructorIdentityDocCard() {
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [docStatus, setDocStatus] = useState<DocStatus>('not_submitted')
  const [docNote, setDocNote] = useState<string | null>(null)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    if (!user?.id) return
    setLoading(true)
    try {
      // Lê o status do registro do instrutor
      const { data: inst } = await supabase
        .from('instructors')
        .select('identity_doc_status, identity_doc_note, identity_doc_url')
        .eq('profile_id', user.id)
        .maybeSingle()

      const row = inst as {
        identity_doc_status?: DocStatus
        identity_doc_note?: string | null
        identity_doc_url?: string | null
      } | null

      const status: DocStatus = row?.identity_doc_status ?? 'not_submitted'
      setDocStatus(status)
      setDocNote(row?.identity_doc_note ?? null)

      // Se tiver URL armazenada, tenta gerar signed URL
      if (row?.identity_doc_url) {
        const { data: signed } = await supabase.storage
          .from('identity-docs')
          .createSignedUrl(row.identity_doc_url, 60 * 10) // 10 min
        setSignedUrl(signed?.signedUrl ?? null)
      } else {
        setSignedUrl(null)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !user?.id) return

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máx 10 MB).')
      return
    }
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) {
      toast.error('Use JPG, PNG, WEBP ou PDF.')
      return
    }

    setUploading(true)
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg'
      const fileName = `${crypto.randomUUID()}.${ext}`
      const path = `${user.id}/${fileName}`

      const { error: upErr } = await supabase.storage
        .from('identity-docs')
        .upload(path, file, { upsert: false, contentType: file.type })
      if (upErr) throw upErr

      // Atualiza o registro do instrutor com o path e muda status para pending
      const { error: updateErr } = await supabase
        .from('instructors')
        .update({
          identity_doc_url: path,
          identity_doc_status: 'pending',
          identity_doc_note: null,
        })
        .eq('profile_id', user.id)

      if (updateErr) {
        console.warn('[identity-doc] erro ao atualizar status:', updateErr.message)
      }

      toast.success('Documento enviado! Aguarde a verificação da equipe.')
      await refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar documento.')
    } finally {
      setUploading(false)
      if (e.target) e.target.value = ''
    }
  }

  const showBadge = docStatus !== 'not_submitted'

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <IdCard className="h-4 w-4 text-brand-600" />
          Verificação de identidade
        </CardTitle>
        {showBadge && (
          <Badge
            variant="outline"
            className={STATUS_META[docStatus as Exclude<DocStatus, 'not_submitted'>].cls}
          >
            {STATUS_META[docStatus as Exclude<DocStatus, 'not_submitted'>].label}
          </Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Envie uma foto nítida do seu RG, CNH ou passaporte. O documento é armazenado
          de forma segura e privada — apenas você e a equipe MeuDriver têm acesso.
        </p>

        {docStatus === 'rejected' && docNote && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <p className="font-medium">Motivo da rejeição:</p>
            <p>{docNote}</p>
          </div>
        )}

        {docStatus === 'verified' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span>Seu documento foi verificado com sucesso.</span>
          </div>
        )}

        {signedUrl && docStatus !== 'verified' && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Ver documento enviado
          </a>
        )}

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={handleFile}
            data-testid="identity-doc-input"
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading || loading || docStatus === 'verified'}
            onClick={() => fileInputRef.current?.click()}
            className="gap-1.5"
            data-testid="identity-doc-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                {docStatus === 'not_submitted' ? 'Enviar documento' : 'Substituir documento'}
              </>
            )}
          </Button>
          {docStatus === 'verified' && (
            <p className="text-xs text-muted-foreground mt-2">
              Documento verificado. Contate o suporte se precisar atualizá-lo.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
